// Customer-facing invoice view. Rendered at /p/invoice/:token (no auth).
// Designed to look better than what QuickBooks or Xero send customers:
// proper brand, mobile-first single column, clear "How to pay" panel,
// running totals including any credit notes and payments. Browser's
// native print → PDF gives a clean A4 deliverable.

import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Printer, Download, MessageCircle, Mail } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { formatDate, formatMoney } from '../lib/format';
import type { Invoice, InvoiceLine, Organization, Contact, TaxCode, Payment, InvoiceTemplateSettings } from '../lib/types';

interface OrgPaymentInstructions {
  bank_name?: string;
  account_holder?: string;
  account_number?: string;
  branch_code?: string;
  account_type?: string;
  swift?: string;
  swift_code?: string;
  iban?: string;
  payment_instructions?: string;
  reference_hint?: string;
}

const TPL_DEFAULTS: InvoiceTemplateSettings = {
  primary_color: '#0c8f60', accent_color: '#d6a85a',
  paper_color: '#f5efe0', text_color: '#0f1521',
  font_family: 'Fraunces',
  title_invoice: 'Invoice', title_credit_note: 'Credit Note',
  header_layout: 'logo-left',
  show_payment_panel: true, show_payment_history: true, show_vat_column: true,
};

export default function InvoicePublic() {
  const { token } = useParams();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [lines, setLines] = useState<InvoiceLine[]>([]);
  const [org, setOrg] = useState<Organization | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [creditNotes, setCreditNotes] = useState<Invoice[]>([]);
  const [pay, setPay] = useState<OrgPaymentInstructions>({});
  const [tpl, setTpl] = useState<InvoiceTemplateSettings>(TPL_DEFAULTS);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    (async () => {
      const { data: inv } = await supabase
        .from('dodobooks_invoices')
        .select('*')
        .eq('public_token', token)
        .maybeSingle();
      if (!inv) { setErr('Invoice not found. Check the link and try again.'); setLoading(false); return; }
      setInvoice(inv as Invoice);
      const [ls, o, c, tcs, pays, cns, settings, template] = await Promise.all([
        supabase.from('dodobooks_invoice_lines').select('*').eq('invoice_id', (inv as any).id).order('position'),
        supabase.from('dodobooks_organizations').select('*').eq('id', (inv as any).org_id).maybeSingle(),
        supabase.from('dodobooks_contacts').select('*').eq('id', (inv as any).contact_id).maybeSingle(),
        supabase.from('dodobooks_tax_codes').select('*').eq('org_id', (inv as any).org_id),
        supabase.from('dodobooks_payments').select('*').eq('invoice_id', (inv as any).id).order('payment_date'),
        supabase.from('dodobooks_invoices').select('*').eq('applied_to_invoice_id', (inv as any).id),
        supabase.from('dodobooks_settings').select('*').eq('org_id', (inv as any).org_id).eq('key', 'payment_instructions').maybeSingle(),
        supabase.from('dodobooks_invoice_templates').select('settings').eq('org_id', (inv as any).org_id).eq('is_default', true).maybeSingle(),
      ]);
      setLines((ls.data ?? []) as InvoiceLine[]);
      setOrg(o.data as Organization);
      setContact(c.data as Contact);
      setTaxCodes((tcs.data ?? []) as TaxCode[]);
      setPayments((pays.data ?? []) as Payment[]);
      setCreditNotes((cns.data ?? []) as Invoice[]);
      setPay((settings.data as any)?.value ?? {});
      if ((template.data as any)?.settings) setTpl({ ...TPL_DEFAULTS, ...((template.data as any).settings as InvoiceTemplateSettings) });
      setLoading(false);
    })();
  }, [token]);

  const totalPaid = useMemo(
    () => payments.reduce((s, p) => s + Number(p.amount), 0)
        + creditNotes.reduce((s, c) => s + Number(c.total), 0),
    [payments, creditNotes]
  );
  const outstanding = invoice ? Math.max(0, Number(invoice.total) - totalPaid) : 0;

  if (loading) return <div className="min-h-screen grid place-items-center text-text-mute">Loading invoice…</div>;
  if (err) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-md">
        <div className="text-rust text-sm mb-4">{err}</div>
        <Link to="/" className="text-emerald hover:underline">DodoBooks</Link>
      </div>
    </div>
  );
  if (!invoice || !org || !contact) return null;

  const isCreditNote = invoice.kind === 'credit_note';
  const isPaid = outstanding < 0.005;
  const isOverdue = !isPaid && new Date(invoice.due_date) < new Date();
  const reference = invoice.reference || invoice.number;
  const whatsappMessage = encodeURIComponent(`Hi — about invoice ${invoice.number}: ${window.location.href}`);

  return (
    <div className="min-h-screen bg-ink text-text">
      {/* Web-only top bar — hidden when printing */}
      <header className="border-b border-line bg-ink-2 print:hidden">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs font-mono text-text-mute uppercase tracking-widest">
            <img src="/logo.svg" alt="" width={20} height={20} className="rounded" />
            <span>Powered by DodoBooks</span>
          </div>
          <div className="flex gap-2">
            <button onClick={() => window.print()} className="btn btn-secondary btn-sm"><Printer size={12} /> Print / Save PDF</button>
            <a href={`https://wa.me/?text=${whatsappMessage}`} target="_blank" rel="noopener" className="btn btn-secondary btn-sm"><MessageCircle size={12} /> WhatsApp</a>
            {contact.email && (
              <a href={`mailto:${contact.email}?subject=${encodeURIComponent(`Invoice ${invoice.number}`)}&body=${whatsappMessage}`} className="btn btn-secondary btn-sm"><Mail size={12} /> Email</a>
            )}
          </div>
        </div>
      </header>

      {/* Template-themed invoice surface */}
      <div className="max-w-3xl mx-auto p-6 print:p-0">
        <article className="rounded-xl shadow-2xl print:shadow-none print:rounded-none print:bg-white relative overflow-hidden"
          style={{ background: tpl.paper_color ?? '#f5efe0', color: tpl.text_color ?? '#0f1521' }}>
          {tpl.show_banner && tpl.banner_url && (<img src={tpl.banner_url} alt="" className="w-full h-24 object-cover" />)}
          {/* Status stamp */}
          {(isPaid || isCreditNote) && (
            <div
              className="absolute top-8 right-8 px-4 py-2 italic font-bold text-2xl tracking-wider border-4 rounded-md rotate-[-8deg] print:rotate-[-8deg]"
              style={{
                fontFamily: tpl.font_family ?? 'Fraunces',
                borderColor: isCreditNote ? (tpl.accent_color ?? '#d6a85a') : (tpl.primary_color ?? '#0c8f60'),
                color: isCreditNote ? (tpl.accent_color ?? '#d6a85a') : (tpl.primary_color ?? '#0c8f60'),
                background: 'rgba(255,255,255,0.3)',
              }}
            >
              {isCreditNote ? (tpl.title_credit_note ?? 'CREDIT NOTE').toUpperCase() : 'PAID'}
            </div>
          )}
          {isOverdue && !isPaid && !isCreditNote && (
            <div
              className="absolute top-8 right-8 px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold rounded"
              style={{ background: '#d97757', color: '#fff' }}
            >
              Overdue
            </div>
          )}

          <div className="p-10 print:p-8">
            {/* Header — org → bill to */}
            <div className="grid grid-cols-2 gap-6 mb-10">
              <div>
                {(tpl.logo_url || org.logo_url)
                  ? <img src={(tpl.logo_url || org.logo_url) as string} alt={org.name} className="h-12 mb-3 object-contain object-left" />
                  : (
                    <div className="text-2xl font-bold leading-tight mb-2" style={{ fontFamily: tpl.font_family ?? 'Fraunces' }}>{org.name}</div>
                  )
                }
                <div className="text-xs leading-relaxed" style={{ color: 'rgba(15,21,33,0.7)' }}>
                  {org.legal_name && org.legal_name !== org.name && <div>{org.legal_name}</div>}
                  {org.address && <div>{org.address}</div>}
                  {(org.city || org.province) && <div>{[org.city, org.province, org.postal_code].filter(Boolean).join(', ')}</div>}
                  {org.phone && <div>{org.phone}</div>}
                  {org.email && <div>{org.email}</div>}
                  {org.vat_number && <div className="font-mono mt-1">VAT no. {org.vat_number}</div>}
                  {org.reg_number && <div className="font-mono">Reg. {org.reg_number}</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-bold text-4xl tracking-tight mb-1" style={{ fontFamily: tpl.font_family ?? 'Fraunces', color: tpl.text_color ?? '#0a0f1c' }}>
                  {isCreditNote ? (tpl.title_credit_note ?? 'Credit Note') : (tpl.title_invoice ?? 'Invoice')}
                </div>
                <div className="font-mono text-sm" style={{ color: 'rgba(15,21,33,0.65)' }}>#{invoice.number}</div>
                <div className="mt-6 inline-block text-left">
                  <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.5)' }}>Bill to</div>
                  <div className="font-semibold mt-0.5">{contact.name}</div>
                  {contact.trading_name && <div className="text-xs">{contact.trading_name}</div>}
                  <div className="text-xs leading-relaxed" style={{ color: 'rgba(15,21,33,0.7)' }}>
                    {contact.address && <div>{contact.address}</div>}
                    {(contact.city || contact.province) && <div>{[contact.city, contact.province, contact.postal_code].filter(Boolean).join(', ')}</div>}
                    {contact.email && <div>{contact.email}</div>}
                    {contact.vat_number && <div className="font-mono">VAT {contact.vat_number}</div>}
                  </div>
                </div>
              </div>
            </div>

            {/* Meta strip */}
            <div className="grid grid-cols-3 gap-4 mb-8 py-4 border-y" style={{ borderColor: 'rgba(15,21,33,0.15)' }}>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Issued</div>
                <div className="font-semibold mt-0.5">{formatDate(invoice.issue_date)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Due</div>
                <div className={`font-semibold mt-0.5 ${isOverdue ? 'text-[#d97757]' : ''}`}>{formatDate(invoice.due_date)}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Reference</div>
                <div className="font-semibold mt-0.5 font-mono text-sm">{reference}</div>
              </div>
            </div>

            {/* Line items */}
            <table className="w-full mb-6">
              <thead>
                <tr style={{ borderBottom: '2px solid rgba(15,21,33,0.18)' }}>
                  <th className="text-left py-2 text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Description</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-16" style={{ color: 'rgba(15,21,33,0.55)' }}>Qty</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-28" style={{ color: 'rgba(15,21,33,0.55)' }}>Unit</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-16" style={{ color: 'rgba(15,21,33,0.55)' }}>VAT</th>
                  <th className="text-right py-2 text-[10px] uppercase tracking-widest font-mono w-28" style={{ color: 'rgba(15,21,33,0.55)' }}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const tc = taxCodes.find((t) => t.id === l.tax_code_id);
                  return (
                    <tr key={l.id} style={{ borderBottom: '1px dashed rgba(15,21,33,0.1)' }}>
                      <td className="py-3 align-top">{l.description}</td>
                      <td className="py-3 text-right font-mono tabular-nums">{Number(l.qty).toLocaleString('en-ZA')}</td>
                      <td className="py-3 text-right font-mono tabular-nums">{formatMoney(l.unit_price, invoice.currency)}</td>
                      <td className="py-3 text-right font-mono text-xs" style={{ color: 'rgba(15,21,33,0.65)' }}>{tc ? `${(tc.rate * 100).toFixed(0)}%` : '—'}</td>
                      <td className="py-3 text-right font-mono tabular-nums font-semibold">{formatMoney(l.line_total, invoice.currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Totals */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {invoice.notes && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-mono mb-2" style={{ color: 'rgba(15,21,33,0.55)' }}>Notes</div>
                  <div className="text-sm whitespace-pre-wrap" style={{ color: 'rgba(15,21,33,0.8)' }}>{invoice.notes}</div>
                </div>
              )}
              <div className={invoice.notes ? '' : 'col-start-2'}>
                <div className="flex justify-between py-1.5 text-sm">
                  <span style={{ color: 'rgba(15,21,33,0.65)' }}>Subtotal</span>
                  <span className="font-mono tabular-nums">{formatMoney(invoice.subtotal, invoice.currency)}</span>
                </div>
                <div className="flex justify-between py-1.5 text-sm" style={{ borderBottom: '1px solid rgba(15,21,33,0.15)' }}>
                  <span style={{ color: 'rgba(15,21,33,0.65)' }}>VAT</span>
                  <span className="font-mono tabular-nums">{formatMoney(invoice.tax_total, invoice.currency)}</span>
                </div>
                <div className="flex justify-between py-3 font-serif text-2xl font-bold" style={{ borderBottom: '2px solid rgba(15,21,33,0.18)' }}>
                  <span>Total</span>
                  <span className="font-mono tabular-nums" style={{ color: '#0c8f60' }}>{formatMoney(invoice.total, invoice.currency)}</span>
                </div>
                {(payments.length > 0 || creditNotes.length > 0) && (
                  <div className="flex justify-between py-1.5 text-sm">
                    <span style={{ color: 'rgba(15,21,33,0.65)' }}>Less payments / credits</span>
                    <span className="font-mono tabular-nums">−{formatMoney(totalPaid, invoice.currency)}</span>
                  </div>
                )}
                <div className="flex justify-between py-2 font-serif text-xl font-bold mt-1" style={{ background: isPaid ? 'rgba(12,143,96,0.1)' : 'rgba(214,168,90,0.1)', padding: '12px', borderRadius: '8px' }}>
                  <span>{isPaid ? 'Paid in full' : 'Amount due'}</span>
                  <span className="font-mono tabular-nums" style={{ color: isPaid ? '#0c8f60' : '#d6a85a' }}>{formatMoney(isPaid ? 0 : outstanding, invoice.currency)}</span>
                </div>
              </div>
            </div>

            {/* Payments history (if any, and template allows) */}
            {(tpl.show_payment_history !== false) && (payments.length > 0 || creditNotes.length > 0) && (
              <div className="mb-6 text-sm">
                <div className="text-[10px] uppercase tracking-widest font-mono mb-2" style={{ color: 'rgba(15,21,33,0.55)' }}>Payment history</div>
                <div className="space-y-1">
                  {payments.map((p) => (
                    <div key={p.id} className="flex justify-between" style={{ color: 'rgba(15,21,33,0.75)' }}>
                      <span>{formatDate(p.payment_date)} · Payment received{p.reference ? ` (${p.reference})` : ''}</span>
                      <span className="font-mono tabular-nums">{formatMoney(p.amount, invoice.currency)}</span>
                    </div>
                  ))}
                  {creditNotes.map((c) => (
                    <div key={c.id} className="flex justify-between" style={{ color: 'rgba(15,21,33,0.75)' }}>
                      <span>{formatDate(c.issue_date)} · Credit note {c.number}</span>
                      <span className="font-mono tabular-nums">{formatMoney(c.total, invoice.currency)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* How to pay (template-controlled) */}
            {tpl.show_payment_panel !== false && !isPaid && !isCreditNote && (pay.bank_name || pay.account_number) && (
              <div className="rounded-lg p-5 mt-6" style={{ background: 'rgba(21,196,134,0.08)', border: '1px solid rgba(21,196,134,0.3)' }}>
                <div className="font-serif text-base font-semibold mb-2" style={{ color: '#0c8f60' }}>How to pay</div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {pay.bank_name && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Bank</div><div className="font-semibold">{pay.bank_name}</div></div>)}
                  {pay.account_holder && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account holder</div><div className="font-semibold">{pay.account_holder}</div></div>)}
                  {pay.account_number && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account number</div><div className="font-mono font-semibold">{pay.account_number}</div></div>)}
                  {pay.branch_code && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Branch code</div><div className="font-mono font-semibold">{pay.branch_code}</div></div>)}
                  {pay.account_type && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>Account type</div><div className="font-semibold capitalize">{pay.account_type}</div></div>)}
                  {pay.swift_code && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>SWIFT</div><div className="font-mono font-semibold">{pay.swift_code}</div></div>)}
                  {pay.iban && (<div><div className="text-[10px] uppercase tracking-widest font-mono" style={{ color: 'rgba(15,21,33,0.55)' }}>IBAN</div><div className="font-mono font-semibold">{pay.iban}</div></div>)}
                </div>
                <div className="text-xs mt-3" style={{ color: 'rgba(15,21,33,0.65)' }}>
                  Reference: <span className="font-mono font-semibold">{invoice.number}</span>
                </div>
                {pay.payment_instructions && (
                  <div className="text-xs mt-3 whitespace-pre-wrap" style={{ color: 'rgba(15,21,33,0.65)' }}>{pay.payment_instructions}</div>
                )}
              </div>
            )}

            {/* Terms */}
            {tpl.terms_text && (
              <div className="mt-6 text-xs whitespace-pre-wrap" style={{ color: 'rgba(15,21,33,0.6)' }}>
                <div className="font-mono uppercase tracking-widest mb-1 text-[10px]">Terms</div>
                {tpl.terms_text}
              </div>
            )}

            {/* Footer */}
            {tpl.footer_text && (
              <div className="mt-8 pt-4 border-t text-center text-xs" style={{ borderColor: 'rgba(15,21,33,0.1)', color: 'rgba(15,21,33,0.55)' }}>
                {tpl.footer_text}
              </div>
            )}
          </div>

          {/* Accent stripe */}
          <div className="h-2" style={{ background: tpl.accent_color ?? '#d6a85a' }} />
          </article>

        {/* Powered by */}
        <div className="text-center mt-6 text-[11px] font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>
          Powered by DodoBooks · CloudDodo
        </div>
      </div>
    </div>
  );
}
